import { __awaiter } from "tslib";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import flatpickr from 'flatpickr';
import omit from 'lodash/omit';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import ReactFlatpickr from 'react-flatpickr';
import { styled } from 'styled-components';
import { getDateLabelText, getErrorId, getInfoId, mergeRefs, } from '@hivebrite/utils';
import { s__, t } from '@design-system/i18n';
import { Loader } from '@design-system/loader';
import { ScreenReadersContent } from '@design-system/screen-readers-content';
import { responsive, useBreakpoint, useTheme } from '@design-system/theme';
import { GlobalFlatpickrStyles } from './components/GlobalFlatpickrStyles';
import { InputWrapperComponent } from './components/InputWrapperComponent';
import { attributesToCopy } from './constants';
import { convertDateToInternalFormat } from './utils/convertDateToInternalFormat';
import { getOptions } from './utils/getOptions';
const Flatpickr = ReactFlatpickr.default
    ?
        ReactFlatpickr.default
    : ReactFlatpickr;
if (process.env.NODE_ENV !== 'test') {
    import('flatpickr/dist/themes/airbnb.css');
}
const LoadingIcon = () => _jsx(Loader, { size: 16 });
const Component = ({ animate, ariaLabel: initialAriaLabel, arrayOfEnableDates, clearable, dataTestId, disabled, displayFormat, enableTime, error, icon: IconComponent, id, isStatic, locale: loc, maxDate, minDate, name, onBlur, onChange, onFocus, placeholder, required, value, variant, }, externalRef) => {
    const theme = useTheme();
    const breakpoint = useBreakpoint();
    const iconPaddingValue = theme.space[4] + theme.input[variant].iconSize || 24;
    const hasError = Boolean(error);
    const errorId = getErrorId(id);
    const infoId = getInfoId(id);
    const ariaLabel = initialAriaLabel
        ? getDateLabelText(initialAriaLabel, t)
        : undefined;
    const messageId = [hasError && errorId, infoId]
        .filter(Boolean)
        .join(' ');
    const isClearable = useMemo(() => {
        if ((breakpoint === 'l' || breakpoint === 'xl') && clearable && !disabled) {
            if (value === undefined) {
                return false;
            }
            return (value === null || value === void 0 ? void 0 : value.toString().trim()) !== '';
        }
        return false;
    }, [breakpoint, clearable, disabled, value]);
    const internalRef = useRef(null);
    const ref = useMemo(() => mergeRefs([externalRef, internalRef]), [externalRef]);
    const instanceRef = useRef(null);
    const [isFocus, setIsFocus] = useState(false);
    const [loading, setLoading] = useState(true);
    const [locale, setLocale] = useState(/^(?:en(?:-NA)?|deepbloo)$/u.test(loc) ? 'en' : loc);
    const fakeInputProps = useMemo(() => ({
        'aria-describedby': messageId,
        'aria-invalid': hasError,
        'aria-label': ariaLabel,
        'aria-required': required,
        className: value ? '' : 'empty',
        disabled,
        id,
        name,
        placeholder,
    }), [
        ariaLabel,
        disabled,
        hasError,
        id,
        messageId,
        name,
        placeholder,
        required,
        value,
    ]);
    const handleChange = useCallback((dates) => {
        var _a, _b;
        const result = convertDateToInternalFormat(dates);
        const el = instanceRef.current;
        if (el) {
            if (result) {
                (_a = el.input.nextElementSibling) === null || _a === void 0 ? void 0 : _a.classList.remove('empty');
            }
            else {
                (_b = el.input.nextElementSibling) === null || _b === void 0 ? void 0 : _b.classList.add('empty');
            }
        }
        onChange === null || onChange === void 0 ? void 0 : onChange(result);
    }, [onChange]);
    const handleBlur = useCallback((dates) => {
        handleChange(dates);
        setIsFocus(false);
        if (dates === null || dates === void 0 ? void 0 : dates[0]) {
            onBlur === null || onBlur === void 0 ? void 0 : onBlur(dates[0].toString());
        }
    }, [handleChange, onBlur]);
    const handleCreate = useCallback((instance) => {
        if (!instance) {
            return;
        }
        instanceRef.current = instance;
        const { altInput, input } = instance;
        if (altInput) {
            if (input.id) {
                altInput.id = id;
                input.id = `${id}_hidden`;
            }
            for (const attr of attributesToCopy) {
                const val = input.getAttribute(attr);
                if (val !== null) {
                    altInput.setAttribute(attr, val);
                }
            }
            input.setAttribute('aria-label', '');
        }
    }, [id]);
    const handleFocus = useCallback((e) => {
        setIsFocus(true);
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
    }, [onFocus]);
    const options = useMemo(() => getOptions({
        animate,
        arrayOfEnableDates,
        displayFormat,
        enableTime,
        isStatic,
        locale,
        maxDate,
        minDate,
    }), [
        animate,
        arrayOfEnableDates,
        displayFormat,
        enableTime,
        isStatic,
        locale,
        maxDate,
        minDate,
    ]);
    const handleRender = useCallback((handleRenderProps, inputRef) => {
        var _a, _b;
        return (_jsxs(_Fragment, { children: [_jsx(FakeInput, Object.assign({}, fakeInputProps, omit(handleRenderProps, ['value']), { autoComplete: "off", ref: inputRef })), _jsx(ScreenReadersContent, { id: infoId, children: s__('Enter the date with your keyboard in the following format: %{format}').i__({
                        format: ((_b = (_a = window === null || window === void 0 ? void 0 : window.I18n) === null || _a === void 0 ? void 0 : _a.t) === null || _b === void 0 ? void 0 : _b.call(_a, 'date.formats.date_picker')) || '',
                    }) })] }));
    }, [fakeInputProps, infoId]);
    useEffect(() => {
        setLoading(true);
        const loadLocal = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const locValue = yield import(`flatpickr/dist/l10n/${loc}.js`);
                if (ref) {
                    flatpickr(ref, { locale: locValue });
                }
            }
            catch (_a) {
                setLocale('en');
            }
            setLoading(false);
        });
        if (loc.startsWith('en')) {
            setLocale('en');
            setLoading(false);
        }
        else {
            loadLocal();
        }
    }, [loc, ref]);
    return (_jsxs(_Fragment, { children: [process.env.NODE_ENV !== 'test' && (_jsx(GlobalFlatpickrStyles, { IconComponent: IconComponent, iconPaddingValue: iconPaddingValue, isClearable: isClearable })), _jsx(InputWrapperComponent, { dataTestId: dataTestId, hasError: hasError, IconComponent: loading && IconComponent ? LoadingIcon : IconComponent, iconPaddingValue: iconPaddingValue, isClearable: isClearable, isFocus: isFocus, onChange: onChange, ref: ref, variant: variant, children: loading ? null : (_jsx(Flatpickr, { onBlur: handleBlur, onClose: handleBlur, onCreate: handleCreate, onFocus: handleFocus, onOpen: () => onFocus, options: options, ref: ref, render: handleRender, value: value || '' })) })] }));
};
export const DatePicker = forwardRef(Component);
const FakeInput = styled.input `
  &::-webkit-date-and-time-value {
    text-align: left;
  }

  &.empty:before {
    color: ${({ theme }) => theme.input.borderColor};
    content: attr(placeholder);
    width: 100%;
  }

  &::placeholder {
    color: ${({ theme }) => theme.input.placeholder};
    // Do not display placeholder on mobile & tablette - using native Date inputs
    width: 0px;
    ${responsive('l') `
      width: 100%;
    `}
  }
`;
//# sourceMappingURL=DatePicker.js.map